import {
  Timestamp,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { functionsClient } from "./firebase";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  PublicIdentitySnapshot,
  VerificationStatus,
} from "../types/identity";
import { isAccountType, isVerificationStatus, normalizeEntitlements } from "./identity";

export type Conversation = {
  id: string;
  members: string[];
  type?: "dm" | "group";
  linkedGroupId?: string;
  name?: string;
  title?: string;
  description?: string;
  subject?: string;
  isPrivate?: boolean;
  admins?: string[];
  whoCanPost?: "all_members" | "admins_only";
  photoURL?: string | null;
  lastMessageText?: string;
  lastMessageAt?: Timestamp;
  lastMessageSenderUid?: string;
  createdAt?: Timestamp;
  createdBy?: string;
  lastReadAt?: Record<string, Timestamp>;
  hiddenFor?: string[];
};

export type Message = {
  id: string;
  senderUid: string;
  text: string;
  type?: "text" | "shared_post";
  sharedPost?: {
    postId: string;
    authorIdentityId?: string;
    authorSnapshot?: PublicIdentitySnapshot;
    authorUsername?: string;
    authorEmail?: string;
    authorAccountType?: AccountType;
    authorVerificationStatus?: VerificationStatus;
    authorEntitlements?: IdentityEntitlements;
    /** @deprecated Compatibility snapshot for historical student/teacher posts. */
    authorRole?: LegacyAccountType;
    subject?: string;
    text?: string;
    groupName?: string;
    imageUrl?: string;
    attachmentType?: "image" | "pdf";
    attachmentName?: string;
  };
  createdAt?: Timestamp;
};

type SharedPostInput = {
  postId: string;
  authorIdentityId?: string;
  authorSnapshot?: PublicIdentitySnapshot;
  authorUsername?: string;
  authorEmail?: string;
  authorAccountType?: AccountType;
  authorVerificationStatus?: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  /** @deprecated Compatibility snapshot for historical student/teacher posts. */
  authorRole?: LegacyAccountType;
  subject?: string;
  text?: string;
  groupName?: string;
  imageUrl?: string;
  attachmentType?: "image" | "pdf";
  attachmentName?: string;
};

type ChatWritePhase =
  | "conversation_get"
  | "conversation_create"
  | "conversation_reopen"
  | "message_create"
  | "conversation_meta_update";

type ChatWriteError = Error & {
  code?: string;
  phase?: ChatWritePhase;
  conversationId?: string;
  messageCreated?: boolean;
  originalMessage?: string;
};

const db = getFirestore();

type ConversationStore = {
  items: Conversation[];
  ready: boolean;
  subscribers: Set<(items: Conversation[]) => void>;
  errorSubscribers: Set<(error: any) => void>;
  unsubscribe: (() => void) | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
};

const conversationStores = new Map<string, ConversationStore>();
const readWriteState = new Map<string, { lastStartedAt: number; pending: Promise<void> | null }>();

function sortConversations(items: Conversation[]) {
  return [...items].sort((a, b) => {
    const aTime = toMillis(a.lastMessageAt ?? a.createdAt);
    const bTime = toMillis(b.lastMessageAt ?? b.createdAt);
    return bTime - aTime;
  });
}

function attachConversationStoreListener(myUid: string, store: ConversationStore) {
  if (store.unsubscribe) return;

  store.unsubscribe = listenMyConversations(
    myUid,
    (items) => {
      const current = conversationStores.get(myUid);
      if (current !== store) return;

      store.items = items;
      store.ready = true;
      store.subscribers.forEach((subscriber) => subscriber(items));
    },
    (error) => {
      const current = conversationStores.get(myUid);
      if (current !== store) return;

      // Firestore listeners are terminal after an error. Release the handle so
      // a future subscriber, refresh, or the bounded retry can attach one
      // listener again instead of keeping a dead subscription in the store.
      store.unsubscribe = null;
      store.ready = false;
      store.errorSubscribers.forEach((subscriber) => subscriber(error));

      if (store.subscribers.size && !store.retryTimer) {
        store.retryTimer = setTimeout(() => {
          store.retryTimer = null;
          if (conversationStores.get(myUid) === store && store.subscribers.size) {
            attachConversationStoreListener(myUid, store);
          }
        }, 2000);
      }
    }
  );
}

function compactUndefined<T extends Record<string, any>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined)
  ) as T;
}

function cleanSharedPostString(value: unknown, maxLength: number) {
  if (typeof value !== "string") return "";
  const clean = value.trim();
  return clean.length <= maxLength ? clean : clean.slice(0, maxLength);
}

function cleanSharedPostAttachmentType(value: unknown) {
  return value === "image" || value === "pdf" ? value : undefined;
}

function buildSafeSharedPost(sharedPost: SharedPostInput, includeImagePreview: boolean) {
  const imageUrl = includeImagePreview
    ? cleanSharedPostString(sharedPost.imageUrl, 2048)
    : "";

  return compactUndefined({
    postId: cleanSharedPostString(sharedPost.postId, 512),
    authorIdentityId: cleanSharedPostString(sharedPost.authorIdentityId, 256),
    authorSnapshot: sharedPost.authorSnapshot,
    authorUsername: cleanSharedPostString(sharedPost.authorUsername, 32),
    authorEmail: cleanSharedPostString(sharedPost.authorEmail, 320),
    authorAccountType: isAccountType(sharedPost.authorAccountType)
      ? sharedPost.authorAccountType
      : undefined,
    authorVerificationStatus: isVerificationStatus(sharedPost.authorVerificationStatus)
      ? sharedPost.authorVerificationStatus
      : undefined,
    authorEntitlements: normalizeEntitlements(sharedPost.authorEntitlements),
    authorRole: sharedPost.authorRole,
    subject: cleanSharedPostString(sharedPost.subject, 64),
    text: cleanSharedPostString(sharedPost.text, 3000),
    groupName: cleanSharedPostString(sharedPost.groupName, 60),
    imageUrl,
    attachmentType: cleanSharedPostAttachmentType(sharedPost.attachmentType),
    attachmentName: cleanSharedPostString(sharedPost.attachmentName, 200),
  });
}

function createChatWriteError(
  error: any,
  phase: ChatWritePhase,
  conversationId: string,
  messageCreated: boolean
): ChatWriteError {
  const nextError = new Error(String(error?.message ?? error ?? "Chat write failed.")) as ChatWriteError;
  nextError.name = "ChatWriteError";
  nextError.code = typeof error?.code === "string" ? error.code : undefined;
  nextError.phase = phase;
  nextError.conversationId = conversationId;
  nextError.messageCreated = messageCreated;
  nextError.originalMessage = String(error?.message ?? error ?? "");
  return nextError;
}

export function createChatMessageId() {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

export function groupConversationId(groupId: string) {
  return `group_${String(groupId || "").trim()}`;
}

function conversationDeletedAtKey(myUid: string, conversationId: string) {
  return `chat_deleted_at:${myUid}:${conversationId}`;
}

export function getChatErrorMessage(error: any, fallback = "Une erreur est survenue.") {
  const code = String(error?.code ?? "");
  const message = String(error?.message ?? error ?? "");
  const lower = message.toLowerCase();

  if (code === "permission-denied" || lower.includes("permission-denied")) {
    return "Accès refusé.";
  }

  if (code === "failed-precondition" || lower.includes("requires an index")) {
    return "Service momentanément indisponible. Réessaie dans un instant.";
  }

  if (code === "unavailable" || lower.includes("network") || lower.includes("offline")) {
    return "Connexion indisponible. Réessaie dans un instant.";
  }

  if (code === "not-found") {
    return "Conversation introuvable.";
  }

  return fallback;
}

export async function createGroupConversation(params: {
  creatorUid: string;
  memberUids: string[];
  name: string;
  description?: string;
  subject?: string;
  isPrivate?: boolean;
}) {
  const {
    creatorUid,
    memberUids,
    name,
    description = "",
    subject = "general",
    isPrivate = true,
  } = params;

  const cleanName = name.trim();
  if (cleanName.length < 3) {
    throw new Error("Le nom du groupe doit contenir au moins 3 caracteres.");
  }

  const members = Array.from(new Set([creatorUid, ...memberUids])).filter(Boolean);

  if (members.length < 2) {
    throw new Error("Il faut au moins 2 membres dans le groupe.");
  }

  const ref = doc(collection(db, "conversations"));

  try {
    const initialLastReadAt = Object.fromEntries(
      members.map((memberUid) => [memberUid, serverTimestamp()])
    );

    await setDoc(ref, {
      type: "group",
      name: cleanName,
      title: cleanName,
      description: description.trim(),
      subject,
      isPrivate,
      members,
      admins: [creatorUid],
      whoCanPost: "all_members",
      createdBy: creatorUid,
      createdAt: serverTimestamp(),
      lastMessageText: "",
      lastMessageAt: null,
      lastMessageSenderUid: null,
      lastReadAt: initialLastReadAt,
      photoURL: null,
      hiddenFor: [],
    });
  } catch (error: any) {
    throw createChatWriteError(error, "conversation_create", ref.id, false);
  }

  return ref.id;
}

export async function ensureLinkedGroupConversation(params: {
  groupId: string;
  creatorUid: string;
  memberUids: string[];
  adminUids?: string[];
  name: string;
  description?: string;
  subject?: string;
  isPublic?: boolean;
  whoCanPost?: "all_members" | "admins_only";
}) {
  const conversationId = groupConversationId(params.groupId);
  const convRef = doc(db, "conversations", conversationId);

  const members = Array.from(new Set(params.memberUids.filter(Boolean)));
  const admins = Array.from(new Set((params.adminUids ?? [params.creatorUid]).filter(Boolean)));

  if (!members.length) {
    throw new Error("Impossible de lancer la discussion sans membres.");
  }

  const basePayload = {
    type: "group" as const,
    linkedGroupId: params.groupId,
    name: params.name.trim() || "Groupe",
    title: params.name.trim() || "Groupe",
    description: (params.description ?? "").trim(),
    subject: params.subject ?? "general",
    isPrivate: !(params.isPublic ?? true),
    members,
    admins: admins.length ? admins : [params.creatorUid],
    whoCanPost: params.whoCanPost ?? "all_members",
    hiddenFor: [],
  };

  // A missing conversation cannot be read under the membership-based rules.
  // This function is called only from the "create discussion" path, so write
  // the deterministic document directly instead of probing it first.
  await setDoc(convRef, {
    ...basePayload,
    createdBy: params.creatorUid,
    createdAt: serverTimestamp(),
    lastMessageText: "",
    lastMessageAt: null,
    lastMessageSenderUid: null,
    photoURL: null,
    lastReadAt: Object.fromEntries(members.map((uid) => [uid, serverTimestamp()])),
  });

  return conversationId;
}

export async function syncLinkedGroupConversationMember(params: {
  groupId: string;
  memberUid: string;
  action: "add" | "remove";
  isAdmin?: boolean;
}) {
  const convRef = doc(db, "conversations", groupConversationId(params.groupId));
  const snap = await getDoc(convRef);

  if (!snap.exists()) return;

  if (params.action === "add") {
    const payload: Record<string, unknown> = {
      members: arrayUnion(params.memberUid),
      hiddenFor: arrayRemove(params.memberUid),
      [`lastReadAt.${params.memberUid}`]: serverTimestamp(),
    };

    if (params.isAdmin) {
      payload.admins = arrayUnion(params.memberUid);
    }

    await updateDoc(convRef, payload);
    return;
  }

  await updateDoc(convRef, {
    members: arrayRemove(params.memberUid),
    admins: arrayRemove(params.memberUid),
  });
}

export async function syncLinkedGroupConversationAdminRole(params: {
  groupId: string;
  memberUid: string;
  isAdmin: boolean;
}) {
  const convRef = doc(db, "conversations", groupConversationId(params.groupId));
  const snap = await getDoc(convRef);

  if (!snap.exists()) return;

  await updateDoc(convRef, {
    admins: params.isAdmin
      ? arrayUnion(params.memberUid)
      : arrayRemove(params.memberUid),
  });
}

export async function syncLinkedGroupConversationMetadata(params: {
  groupId: string;
  name: string;
  description?: string;
  subject?: string;
  isPublic?: boolean;
}) {
  const convRef = doc(db, "conversations", groupConversationId(params.groupId));
  const snap = await getDoc(convRef);

  if (!snap.exists()) return;

  await updateDoc(convRef, {
    name: params.name.trim() || "Groupe",
    title: params.name.trim() || "Groupe",
    description: (params.description ?? "").trim(),
    subject: params.subject ?? "general",
    isPrivate: !(params.isPublic ?? true),
  });
}

export async function updateLinkedGroupConversationPostingMode(params: {
  groupId: string;
  whoCanPost: "all_members" | "admins_only";
}) {
  await updateDoc(doc(db, "conversations", groupConversationId(params.groupId)), {
    whoCanPost: params.whoCanPost,
  });
}

export async function deleteLinkedGroupConversation(groupId: string) {
  const conversationId = groupConversationId(groupId);
  const conversationRef = doc(db, "conversations", conversationId);
  const conversationSnap = await getDoc(conversationRef);
  if (!conversationSnap.exists()) return;

  const messagesSnap = await getDocs(collection(db, "conversations", conversationId, "messages"));
  const chunkSize = 400;

  for (let index = 0; index < messagesSnap.docs.length; index += chunkSize) {
    const batch = writeBatch(db);
    const chunk = messagesSnap.docs.slice(index, index + chunkSize);
    chunk.forEach((messageDoc) => {
      batch.delete(doc(db, "conversations", conversationId, "messages", messageDoc.id));
    });
    await batch.commit();
  }

  await deleteDoc(conversationRef);
}

export async function sendMessage(
  conversationId: string,
  senderUid: string,
  text: string,
  messageId?: string
) {
  const t = text.trim();
  if (!t) return;
  if (t.length > 2000) return;

  const convRef = doc(db, "conversations", conversationId);
  const messageRef = messageId
    ? doc(db, "conversations", conversationId, "messages", messageId)
    : doc(collection(db, "conversations", conversationId, "messages"));

  try {
    if (messageId) {
      const existingMessage = await getDoc(messageRef);
      if (existingMessage.exists()) return;
    }

    const batch = writeBatch(db);
    batch.set(messageRef, {
      senderUid,
      text: t,
      type: "text",
      createdAt: serverTimestamp(),
    });
    batch.update(convRef, {
      lastMessageText: t,
      lastMessageAt: serverTimestamp(),
      lastMessageSenderUid: senderUid,
      [`lastReadAt.${senderUid}`]: serverTimestamp(),
      hiddenFor: [],
    });
    await batch.commit();
  } catch (error: any) {
    throw createChatWriteError(error, "message_create", conversationId, false);
  }
}

export async function sendSharedPostMessage(
  conversationId: string,
  senderUid: string,
  sharedPost: SharedPostInput
) {
  const postId = String(sharedPost?.postId ?? "").trim();
  if (!postId) return;

  const previewText = String(sharedPost?.text ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 140);

  const convRef = doc(db, "conversations", conversationId);
  const lastMessageText = previewText ? `Publication partagée : ${previewText}` : "Publication partagée";
  const safeSharedPost = buildSafeSharedPost(sharedPost, true);

  async function commitSharedPostMessage(payload: Record<string, any>) {
    const messageRef = doc(collection(db, "conversations", conversationId, "messages"));
    const batch = writeBatch(db);
    batch.set(messageRef, {
      senderUid,
      text: lastMessageText,
      type: "shared_post",
      sharedPost: payload,
      createdAt: serverTimestamp(),
    });
    batch.update(convRef, {
      lastMessageText,
      lastMessageAt: serverTimestamp(),
      lastMessageSenderUid: senderUid,
      [`lastReadAt.${senderUid}`]: serverTimestamp(),
      hiddenFor: [],
    });
    await batch.commit();
  }

  try {
    await commitSharedPostMessage(safeSharedPost);
  } catch (error: any) {
    if (safeSharedPost.imageUrl || safeSharedPost.attachmentType === "image") {
      try {
        await commitSharedPostMessage(buildSafeSharedPost(sharedPost, false));
        return;
      } catch {
        // Surface the original write error below.
      }
    }

    throw createChatWriteError(error, "message_create", conversationId, false);
  }
}

export async function markConversationRead(conversationId: string, myUid: string) {
  const key = `${myUid}:${conversationId}`;
  const now = Date.now();
  const current = readWriteState.get(key) ?? { lastStartedAt: 0, pending: null };

  if (current.pending) return current.pending;
  if (now - current.lastStartedAt < 1500) return;

  current.lastStartedAt = now;
  const convRef = doc(db, "conversations", conversationId);
  current.pending = updateDoc(convRef, {
    [`lastReadAt.${myUid}`]: serverTimestamp(),
  }).finally(() => {
    const latest = readWriteState.get(key);
    if (latest) latest.pending = null;
  });
  readWriteState.set(key, current);
  return current.pending;
}

export function subscribeMyConversations(
  myUid: string,
  cb: (items: Conversation[]) => void,
  onErr?: (error: any) => void
) {
  let store = conversationStores.get(myUid);

  if (!store) {
    store = {
      items: [],
      ready: false,
      subscribers: new Set(),
      errorSubscribers: new Set(),
      unsubscribe: null,
      retryTimer: null,
    };
    conversationStores.set(myUid, store);
  }

  attachConversationStoreListener(myUid, store);
  store.subscribers.add(cb);
  if (onErr) store.errorSubscribers.add(onErr);
  if (store.ready) cb(store.items);

  return () => {
    const current = conversationStores.get(myUid);
    if (!current) return;
    current.subscribers.delete(cb);
    if (onErr) current.errorSubscribers.delete(onErr);

    if (!current.subscribers.size && !current.errorSubscribers.size) {
      if (current.retryTimer) {
        clearTimeout(current.retryTimer);
        current.retryTimer = null;
      }
      current.unsubscribe?.();
      conversationStores.delete(myUid);
    }
  };
}

export function refreshMyConversations(myUid: string) {
  const store = conversationStores.get(myUid);
  if (!store) return;

  if (store.retryTimer) {
    clearTimeout(store.retryTimer);
    store.retryTimer = null;
  }
  store.unsubscribe?.();
  store.unsubscribe = null;
  store.ready = false;
  attachConversationStoreListener(myUid, store);
}

export async function hideConversationForUser(conversationId: string, myUid: string) {
  const convRef = doc(db, "conversations", conversationId);
  await updateDoc(convRef, {
    hiddenFor: arrayUnion(myUid),
  });
}

export async function deleteConversationForUser(params: {
  conversationId: string;
  myUid: string;
  type?: "dm" | "group";
}) {
  await hideConversationForUser(params.conversationId, params.myUid);

  const key = conversationDeletedAtKey(params.myUid, params.conversationId);

  if (params.type === "dm") {
    await AsyncStorage.setItem(key, String(Date.now()));
    return;
  }

  await AsyncStorage.removeItem(key);
}

export async function getConversationDeletedAt(myUid: string, conversationId: string) {
  const raw = await AsyncStorage.getItem(conversationDeletedAtKey(myUid, conversationId));
  const value = Number(raw || 0);
  return Number.isFinite(value) ? value : 0;
}

export async function getConversationDeletedAtMap(myUid: string, conversationIds: string[]) {
  if (!conversationIds.length) return {};

  const pairs = await AsyncStorage.multiGet(
    conversationIds.map((conversationId) => conversationDeletedAtKey(myUid, conversationId))
  );

  const map: Record<string, number> = {};

  conversationIds.forEach((conversationId, index) => {
    const [, raw] = pairs[index] ?? [];
    const value = Number(raw || 0);
    map[conversationId] = Number.isFinite(value) ? value : 0;
  });

  return map;
}

async function getConversation(conversationId: string) {
  const convRef = doc(db, "conversations", conversationId);
  const snap = await getDoc(convRef);

  if (!snap.exists()) {
    throw new Error("Conversation introuvable.");
  }

  return {
    ref: convRef,
    data: snap.data() as Conversation,
  };
}

export function listenMyConversations(
  myUid: string,
  cb: (items: Conversation[]) => void,
  onErr?: (e: any) => void
) {
  const qy = query(
    collection(db, "conversations"),
    where("members", "array-contains", myUid),
    limit(100)
  );

  return onSnapshot(
    qy,
    (snap) => {
      const items = sortConversations(
        snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }))
      );
      cb(items);
    },
    onErr
  );
}

export function listenMessages(
  conversationId: string,
  cb: (items: Message[]) => void,
  onErr?: (e: any) => void
) {
  const qy = query(
    collection(db, "conversations", conversationId, "messages"),
    orderBy("createdAt", "desc"),
    limit(30)
  );

  return onSnapshot(
    qy,
    (snap) => {
      const items: Message[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));
      cb(items.reverse());
    },
    onErr
  );
}

export async function loadOlderMessages(conversationId: string, oldestMessageDoc: any) {
  const qy = query(
    collection(db, "conversations", conversationId, "messages"),
    orderBy("createdAt", "desc"),
    startAfter(oldestMessageDoc),
    limit(30)
  );

  const snap = await getDocs(qy);

  const items: Message[] = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as any),
  }));

  return {
    messages: items.reverse(),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
  };
}

export async function backfillMyConversations(myUid: string) {
  const qy = query(
    collection(db, "conversations"),
    where("members", "array-contains", myUid),
    limit(200)
  );

  const snap = await getDocs(qy);

  const fixes: Promise<any>[] = [];

  snap.docs.forEach((d) => {
    const data: any = d.data();

    if (!data.lastMessageAt) {
      fixes.push(
        updateDoc(doc(db, "conversations", d.id), {
          lastMessageAt: data.createdAt || serverTimestamp(),
          lastMessageText: data.lastMessageText ?? "",
        })
      );
    }
  });

  await Promise.allSettled(fixes);
}

export function toMillis(x: any): number {
  if (!x) return 0;
  if (typeof x === "number") return x;
  if (typeof x?.toMillis === "function") return x.toMillis();
  if (typeof x?.seconds === "number") return x.seconds * 1000;
  return 0;
}

export async function loadMyConversationsFallback() {
  const callable = httpsCallable(functionsClient, "listMyConversations");
  const result = await callable();
  const items = Array.isArray((result.data as any)?.items) ? (result.data as any).items : [];
  return items as Conversation[];
}

export function isConversationUnread(conv: any, myUid: string) {
  const hiddenFor = Array.isArray(conv?.hiddenFor) ? conv.hiddenFor : [];
  if (hiddenFor.includes(myUid) || conv?.lastMessageSenderUid === myUid) return false;

  const lastMsg = toMillis(conv?.lastMessageAt);
  const lastRead = toMillis(conv?.lastReadAt?.[myUid]);
  if (!lastMsg) return false;
  return lastRead === 0 || lastMsg > lastRead;
}
export async function leaveGroupConversation(conversationId: string, myUid: string) {
  const { ref: convRef, data } = await getConversation(conversationId);
  const members = Array.isArray(data.members) ? data.members : [];
  const admins = Array.isArray(data.admins) ? data.admins : [];
  const isAdmin = admins.includes(myUid);
  const remainingMembers = members.filter((uid) => uid !== myUid);
  const remainingAdmins = admins.filter((uid) => uid !== myUid);

  if (!members.includes(myUid)) {
    throw new Error("Tu ne fais pas partie de ce groupe.");
  }

  if (isAdmin && remainingMembers.length > 0 && remainingAdmins.length === 0) {
    throw new Error("Ajoute un autre admin avant de quitter le groupe.");
  }

  await updateDoc(convRef, {
    members: arrayRemove(myUid),
    admins: arrayRemove(myUid),
  });
}

export async function addMembersToGroupConversation(
  conversationId: string,
  memberUids: string[]
) {
  const clean = Array.from(new Set(memberUids)).filter(Boolean);
  if (!clean.length) return;

  const { ref: convRef } = await getConversation(conversationId);

  const payload: Record<string, unknown> = {
    members: arrayUnion(...clean),
  };

  clean.forEach((uid) => {
    payload[`lastReadAt.${uid}`] = serverTimestamp();
  });

  await updateDoc(convRef, payload);
}

export async function removeMemberFromGroupConversation(
  conversationId: string,
  memberUid: string
) {
  const { ref: convRef, data } = await getConversation(conversationId);
  const members = Array.isArray(data.members) ? data.members : [];
  const admins = Array.isArray(data.admins) ? data.admins : [];
  const isAdminTarget = admins.includes(memberUid);
  const remainingMembers = members.filter((uid) => uid !== memberUid);
  const remainingAdmins = admins.filter((uid) => uid !== memberUid);

  if (!members.includes(memberUid)) {
    throw new Error("Cet utilisateur ne fait pas partie du groupe.");
  }

  if (isAdminTarget && remainingMembers.length > 0 && remainingAdmins.length === 0) {
    throw new Error("Ce groupe doit garder au moins un admin.");
  }

  await updateDoc(convRef, {
    members: arrayRemove(memberUid),
    admins: arrayRemove(memberUid),
  });
}
